/**
 * Enum for referral campaign types.
 * Used to track which campaign a referral link belongs to.
 */
export enum ReferralCampaign {
  evidence = 'evidence',
}

const REF_CODE_STORAGE_KEY = 'heidi_ref_code';
const REFERRAL_CAMPAIGN_STORAGE_KEY = 'heidi_referral_campaign';

/**
 * Extracts affiliate/referral code from URL query parameters.
 * Checks common affiliate tracking parameters used by Rewardful and other platforms.
 *
 * @param searchParams - URLSearchParams object from window.location.search
 * @returns The affiliate code if found, undefined otherwise
 *
 * @example
 * // URL: https://scribe.heidihealth.com/register?via=412e0e
 * const code = getAffiliateCodeFromUrl(); // Returns: "412e0e"
 */
export function getAffiliateCodeFromUrl(
  searchParams?: URLSearchParams
): string | undefined {
  const params = searchParams || new URLSearchParams(window.location.search);

  // Check common affiliate parameter names
  // 'via' is used by Rewardful (primary)
  // 'ref' and 'affiliate' are common alternatives
  return params.get('via') || params.get('ref') || params.get('affiliate') || undefined;
}

/**
 * Extracts referral code from URL query parameters.
 * This is a generic referral code that works with any campaign.
 *
 * @param searchParams - URLSearchParams object from window.location.search
 * @returns The referral code if found, undefined otherwise
 *
 * @example
 * // URL: https://scribe.heidihealth.com/onboarding?ref_code=ABC123XYZ&campaign=evidence
 * const code = getRefCodeFromUrl(); // Returns: "ABC123XYZ"
 */
export function getRefCodeFromUrl(searchParams?: URLSearchParams): string | undefined {
  const params = searchParams || new URLSearchParams(window.location.search);
  return params.get('ref_code') || undefined;
}

/**
 * Stores referral code in localStorage to persist across auth redirects.
 * The code is captured before auth and retrieved after.
 */
export function storeRefCode(code: string): void {
  try {
    localStorage.setItem(REF_CODE_STORAGE_KEY, code);
  } catch {
    // localStorage may not be available (e.g., private browsing)
  }
}

/**
 * Retrieves stored referral code from localStorage.
 * Returns undefined if not found or localStorage is unavailable.
 */
export function getStoredRefCode(): string | undefined {
  try {
    return localStorage.getItem(REF_CODE_STORAGE_KEY) || undefined;
  } catch {
    return undefined;
  }
}

/**
 * Clears stored referral code from localStorage.
 * Should be called after the code has been successfully sent to the backend.
 */
export function clearStoredRefCode(): void {
  try {
    localStorage.removeItem(REF_CODE_STORAGE_KEY);
  } catch {
    // localStorage may not be available
  }
}

/**
 * Extracts referral campaign from URL query parameters.
 * Used to track which campaign a referral link belongs to.
 *
 * @param searchParams - URLSearchParams object from window.location.search
 * @returns The campaign if found and valid, undefined otherwise
 *
 * @example
 * // URL: https://scribe.heidihealth.com/onboarding?ref_code=ABC123&campaign=evidence
 * const campaign = getReferralCampaignFromUrl(); // Returns: "evidence"
 */
export function getReferralCampaignFromUrl(
  searchParams?: URLSearchParams
): ReferralCampaign | undefined {
  const params = searchParams || new URLSearchParams(window.location.search);
  const campaign = params.get('campaign');
  // Validate that campaign is a valid ReferralCampaign value
  if (
    campaign &&
    Object.values(ReferralCampaign).includes(campaign as ReferralCampaign)
  ) {
    return campaign as ReferralCampaign;
  }
  return undefined;
}

/**
 * Stores referral campaign in localStorage to persist across auth redirects.
 * The campaign is captured before auth and retrieved after.
 */
export function storeReferralCampaign(campaign: ReferralCampaign): void {
  try {
    localStorage.setItem(REFERRAL_CAMPAIGN_STORAGE_KEY, campaign);
  } catch {
    // localStorage may not be available (e.g., private browsing)
  }
}

/**
 * Retrieves stored referral campaign from localStorage.
 * Returns undefined if not found or localStorage is unavailable.
 */
export function getStoredReferralCampaign(): ReferralCampaign | undefined {
  try {
    const campaign = localStorage.getItem(REFERRAL_CAMPAIGN_STORAGE_KEY);
    if (
      campaign &&
      Object.values(ReferralCampaign).includes(campaign as ReferralCampaign)
    ) {
      return campaign as ReferralCampaign;
    }
    return undefined;
  } catch {
    return undefined;
  }
}

/**
 * Clears stored referral campaign from localStorage.
 * Should be called after the campaign has been successfully sent to the backend.
 */
export function clearStoredReferralCampaign(): void {
  try {
    localStorage.removeItem(REFERRAL_CAMPAIGN_STORAGE_KEY);
  } catch {
    // localStorage may not be available
  }
}
